import dayjs from "dayjs";

export const DEFAULT_COLUMNS = [
  {
    dataKey: "propertyName",
    header: "Property",
    label: "Property",
    width: 150,
  },
  {
    dataKey: "ratePlanDisplayText",
    header: "Rate Plan",
    label: "Rate Plan",
    width: 150,
  },
  {
    dataKey: "promotionName",
    header: "Promotion",
    label: "Promotion",
    width: 150,
  },
  {
    dataKey: "roomName",
    header: "Room type",
    label: "Room type",
    width: 150,
  },
  {
    dataKey: "arrivalDate",
    header: "Arrival",
    label: "Arrival",
    width: 120,
  },
  {
    dataKey: "departureDate",
    header: "Departure",
    label: "Departure",
    width: 120,
  },
  {
    dataKey: "netPrice",
    header: "Net Price",
    label: "Net Price",
    width: 100,
  },
  {
    dataKey: "tax",
    header: "Tax",
    label: "Tax",
    width: 100,
  },
  {
    dataKey: "grossPrice",
    header: "Total price",
    label: "Total price",
    width: 120,
  },
  {
    dataKey: "currency",
    header: "Currency",
    label: "Currency",
    width: 100,
  },
  {
    dataKey: "mealPlanCode",
    header: "Meal Plan",
    label: "Meal Plan",
    width: 150,
  },
  {
    dataKey: "meshed",
    header: "Meshed",
    label: "Meshed",
    width: 90,
  },
  {
    dataKey: "refundable",
    header: "Cancellation policy",
    label: "Cancellation policy",
    width: 200,
  },
];

export const ALL_COLUMNS = [
  ...DEFAULT_COLUMNS,
  {
    dataKey: "bookDate",
    header: "Booking creation date",
    label: "Booking creation date",
    width: 180,
  },
  {
    dataKey: "resortFee",
    header: "Resort fee",
    label: "Resort fee",
    width: 120,
  },
  {
    dataKey: "numberOfNights",
    header: "Number of nights",
    label: "Number of nights",
    width: 150,
  },
];

/**
 * Converts ISO date strings to YYYY-MM-DD format respecting user's local date selection
 * This method is preferred when the ISO string represents a user's local date choice
 * rather than a UTC timestamp that should always be interpreted as UTC.
 *
 * @param {string} isoDate - ISO date string (e.g., "2025-07-14T18:30:00.000Z")
 * @returns {string} - Date in YYYY-MM-DD format respecting user's timezone
 */
export const convertISODateToYMDRespectingUserTimezone = (isoDate) => {
  if (!isoDate) {
    return "";
  }

  // Create date object from ISO string
  const date = new Date(isoDate);

  // Check if the date is valid before proceeding
  if (isNaN(date.getTime())) {
    return "";
  }

  // Note:  We do not timezone offset as extracting date from ISO string which will be in user's local timezone
  return isoDate ? dayjs(isoDate).format("YYYY-MM-DD") : "";
};

/**
 * Formats a date string from YYYY-MM-DD format to MMM-DD-YYYY format
 * @param {string} dateString - Date string in YYYY-MM-DD format (e.g., "2025-09-11")
 * @returns {string} - Formatted date string in MMM-DD-YYYY format (e.g., "Sep-11-2025")
 */
export const formatDateToMMMDDYYYY = (dateString) => {
  if (!dateString || typeof dateString !== "string") {
    return "";
  }

  // Create date object from the date string
  const date = new Date(dateString);

  // Check if the date is valid before proceeding
  if (isNaN(date.getTime())) {
    return "";
  }

  // Use dayjs to format the date to MMM-DD-YYYY format
  return dayjs(dateString).format("MMM-D-YYYY");
};

/**
 * Wrapper function to convert user selected dates array from ISO format to required format
 * This method is preferred when the ISO string represents a user's local date choice
 * In case when UTC date is different then user's local date, this method will return the correct user selected date
 * @param {Array} userSelectedDates - Array of date pairs in ISO format
 * @returns {Array} - Array of date objects with checkInDate and checkOutDate
 */
export const convertUserSelectedDates = (userSelectedDates) => {
  if (!Array.isArray(userSelectedDates)) {
    return [];
  }

  return userSelectedDates
    .map((datePair) => {
      if (!Array.isArray(datePair) || datePair.length !== 2) {
        return null;
      }

      const [checkInISO, checkOutISO] = datePair;

      return {
        checkInDate: convertISODateToYMDRespectingUserTimezone(checkInISO),
        checkOutDate: convertISODateToYMDRespectingUserTimezone(checkOutISO),
      };
    })
    .filter(Boolean); // Remove any null entries
};

export const getSelectedPropertyNames = (
  properties,
  selectedCity,
  selectedProperties
) => {
  const propertyNames = new Set(); // Use Set to avoid duplicates

  // If specific properties are selected, use those
  if (selectedProperties && selectedProperties.length > 0) {
    selectedProperties.forEach((propertyId) => {
      const property = properties.find((p) => p.propertyId === propertyId);
      if (property?.propertyName) {
        propertyNames.add(property.propertyName);
      }
    });
  }
  // If cities are selected, get all properties from those cities
  else if (selectedCity && selectedCity.length > 0) {
    selectedCity.forEach((city) => {
      const cityProperties = properties.filter(
        (property) => property.city === city
      );
      cityProperties.forEach((property) => {
        if (property.propertyName) {
          propertyNames.add(property.propertyName);
        }
      });
    });
  }

  return Array.from(propertyNames).sort(); // Sort alphabetically for consistent display
};

// Merge property lists de-duplicated by propertyId. Earlier lists win on
// conflict, so pass the preferred/fresher source first.
export const mergePropertiesById = (...lists) => {
  const byId = new Map();
  lists.forEach((list) => {
    (list || []).forEach((property) => {
      if (property?.propertyId && !byId.has(property.propertyId)) {
        byId.set(property.propertyId, property);
      }
    });
  });
  return Array.from(byId.values());
};

// Get selected property objects (full property objects) for tabs.
// This ensures properties with same name but different IDs get separate tabs.
export const getSelectedPropertyObjects = (
  properties,
  selectedCity,
  selectedProperties
) => {
  const propertyMap = new Map(); // Use Map with propertyId as key to avoid duplicates

  // If specific properties are selected, use those
  if (selectedProperties && selectedProperties.length > 0) {
    selectedProperties.forEach((propertyId) => {
      const property = properties.find((p) => p.propertyId === propertyId);
      if (property) {
        // Keep the full object (incl. expediaHotelId) so downstream hotel-id
        // resolution works for properties found via server-side search.
        propertyMap.set(property.propertyId, { ...property });
      }
    });
  }
  // If cities are selected, get all properties from those cities
  else if (selectedCity && selectedCity.length > 0) {
    selectedCity.forEach((city) => {
      const cityProperties = properties.filter(
        (property) => property.city === city
      );
      cityProperties.forEach((property) => {
        if (property) {
          propertyMap.set(property.propertyId, { ...property });
        }
      });
    });
  }

  // Return array sorted by property name, then by property ID for consistency
  return Array.from(propertyMap.values()).sort((a, b) => {
    const nameCompare = (a.propertyName || "").localeCompare(
      b.propertyName || ""
    );
    if (nameCompare !== 0) return nameCompare;
    // Convert propertyId to string for comparison (it might be a number)
    const aId = String(a.propertyId || "");
    const bId = String(b.propertyId || "");
    return aId.localeCompare(bId);
  });
};

export const getSelectedPropertyExpediaHotelIds = (
  properties,
  selectedCity,
  selectedProperties
) => {
  const hotelIds = new Set(); // Use Set to avoid duplicates

  // Add properties from selected city
  if (selectedCity && selectedCity.length > 0) {
    selectedCity.forEach((city) => {
      const cityProperties = properties.filter(
        (property) => property.city === city
      );
      cityProperties.forEach((property) => {
        if (property.expediaHotelId) {
          hotelIds.add(property.expediaHotelId);
        }
      });
    });
  }

  // Add specifically selected properties
  if (selectedProperties && selectedProperties.length > 0) {
    selectedProperties.forEach((propertyId) => {
      const property = properties.find((p) => p.propertyId === propertyId);
      if (property?.expediaHotelId) {
        hotelIds.add(property.expediaHotelId);
      }
    });
  }

  return Array.from(hotelIds);
};

// Get hotel ID for a specific property name (for tab switching)
export const getHotelIdForPropertyName = (properties, propertyName) => {
  if (propertyName === "all") {
    return null; // Return null for "all" tab to maintain current behavior
  }

  const property = properties.find((p) => p.propertyName === propertyName);
  return property?.expediaHotelId || null;
};

// Get hotel ID for a specific property ID (for tab switching)
export const getHotelIdForPropertyId = (properties, propertyId) => {
  if (propertyId === "all") {
    return null; // Return null for "all" tab to maintain current behavior
  }

  const property = properties.find((p) => p.propertyId === propertyId);
  return property?.expediaHotelId || null;
};

export const DEFAULT_PAGE_SIZE = 10;

/**
 * Generates a comma-separated age string from adults count and child ages array
 * All adults are assumed to be 30 years old
 *
 * @param {number} adultsCount - Number of adults (defaults to 30 years old each)
 * @param {Array<number>} childAges - Array of child ages
 * @returns {string} - Comma-separated age string (e.g., "30,30,4,4,4")
 */
export const getAgeString = (adultsCount, childAges = []) => {
  // Validate inputs
  if (!adultsCount || adultsCount < 1) {
    return "";
  }

  // Create array of adult ages (all 30)
  const adultAges = Array(adultsCount).fill(30);

  // Filter out invalid child ages (null, undefined, negative)
  const validChildAges = (childAges || []).filter(
    (age) => age !== null && age !== undefined && age >= 0
  );

  // Combine adult and child ages
  const allAges = [...adultAges, ...validChildAges];

  // Return comma-separated string
  return allAges.join(",");
};
